import React, { useState } from 'react';
import {
  ACTION_SIZES,
  ACTION_VARIANTS,
  Button,
  FormField,
  ICON_NAMES,
  INLINE_ALERT_VARIANTS,
  InlineAlert,
  Textarea,
} from '@reverse-hr/design-system';
import { useStoreActions } from 'easy-peasy';
import { selectorRootActions } from '@model/index';
import { useTranslation } from 'react-i18next';

export const NegativeFeedbackModal = ({ onSendNegativeFeedback }) => {
  const { t } = useTranslation();
  const { setModalOpened, setModalId } = useStoreActions(selectorRootActions);

  const handleCloseModal = () => {
    setModalOpened(false);
    setModalId(null);
  };

  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alertError, setAlertError] = useState(null);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);

  const handleSubmit = async () => {
    setAlertError(null);
    setIsLoading(true);

    try {
      await onSendNegativeFeedback(notes);
      setIsFeedbackSent(true);
    } catch (error) {
      setAlertError(
        t(error.message || 'general.error.title', t('general.error.title')),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitButtonDisabled = !notes || isLoading;

  const title = isFeedbackSent
    ? t('candidateDetails.negativeFeedback.successTitle')
    : t('candidateDetails.negativeFeedback.title');

  const subtitle = isFeedbackSent
    ? t('candidateDetails.negativeFeedback.successSubtitle')
    : t('candidateDetails.negativeFeedback.subtitle');

  return (
    <div className="c-app-negative-feedback">
      <div className="c-app-negative-feedback__top-section">
        <div className="c-app-negative-feedback__close">
          <Button
            leftIcon={ICON_NAMES.CLOSE_BIG}
            onClick={handleCloseModal}
            size={ACTION_SIZES.SMALL}
            variant={ACTION_VARIANTS.OUTLINED}
          />
        </div>

        <div className="c-app-negative-feedback__title">{title}</div>
        <div className="c-app-negative-feedback__subtitle">{subtitle}</div>
      </div>

      {isFeedbackSent ? null : (
        <FormField
          id={NOTES_FIELD_ID}
          isRequired
          label={t('candidateDetails.negativeFeedback.notesLabel')}
        >
          <Textarea
            id={NOTES_FIELD_ID}
            onChange={setNotes}
            placeholder={t(
              'candidateDetails.negativeFeedback.notesPlaceholder',
            )}
            rows={4}
            value={notes}
          />
        </FormField>
      )}

      <div className="c-app-negative-feedback__actions">
        {isFeedbackSent ? (
          <Button
            className="c-app-negative-feedback__action"
            onClick={handleCloseModal}
          >
            {t('candidateDetails.negativeFeedback.successClose')}
          </Button>
        ) : (
          <>
            <Button
              className="c-app-negative-feedback__action"
              onClick={handleCloseModal}
              variant={ACTION_VARIANTS.OUTLINED}
            >
              {t('candidateDetails.negativeFeedback.cancel')}
            </Button>
            <Button
              className="c-app-negative-feedback__action"
              isDisabled={isSubmitButtonDisabled}
              onClick={handleSubmit}
            >
              {t('candidateDetails.negativeFeedback.submit')}
            </Button>
          </>
        )}
      </div>

      {alertError && (
        <InlineAlert
          className="c-app-negative-feedback__alert"
          icon={ICON_NAMES.WARNING_OUTLINE}
          isClosable
          isFloating
          isSingleLine
          onClose={() => setAlertError(null)}
          variant={INLINE_ALERT_VARIANTS.ERROR}
        >
          {alertError}
        </InlineAlert>
      )}
    </div>
  );
};

const NOTES_FIELD_ID = 'negative-feedback-notes';
